import React, { useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { XIcon } from './Icons';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Use when modal must appear above other overlays (e.g. z-[110] above a panel at z-[100]) */
  zIndex?: number;
  /** If true, close on Escape and trap focus inside. Default true. */
  closeOnEscape?: boolean;
}

interface ModalHeaderProps {
  children: React.ReactNode;
}

interface ModalTitleProps {
  children: React.ReactNode;
}

interface ModalContentProps {
  children: React.ReactNode;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, size = 'md', zIndex, closeOnEscape = true }) => {
  const { colors } = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap: keep Tab inside modal
  useEffect(() => {
    if (!isOpen || !contentRef.current || !closeOnEscape) return;
    const el = contentRef.current;
    const focusables = el.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first?.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEscape]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div
      className={cn('fixed inset-0 flex items-center justify-center p-4', zIndex == null && 'z-50')}
      style={zIndex != null ? { zIndex } : undefined}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        // Only close if clicking directly on the overlay background, not on modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={(e) => {
          // Prevent clicks on overlay from bubbling up
          e.stopPropagation();
        }}
      />
      <div
        ref={contentRef}
        className={cn(
          'relative z-50 w-full max-h-[90vh] flex flex-col overflow-hidden rounded-2xl shadow-lift',
          colors.surface,
          colors.border,
          'border',
          sizeClasses[size]
        )}
        onClick={(e) => {
          // Stop propagation to prevent closing when clicking inside modal
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Also stop propagation on mousedown to prevent outside click handlers
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </div>
  );
};

const ModalHeader: React.FC<ModalHeaderProps> = ({ children }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('flex shrink-0 items-center justify-between p-6 border-b', colors.border)}>
      {children}
    </div>
  );
};

const ModalTitle: React.FC<ModalTitleProps> = ({ children }) => {
  const { colors } = useTheme();
  return <h2 className={cn('text-lg font-semibold', colors.text)}>{children}</h2>;
};

const ModalContent: React.FC<ModalContentProps> = ({ children }) => {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-6">
      {children}
    </div>
  );
};

const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('flex shrink-0 items-center justify-end gap-3 p-6 border-t', colors.border, className)}>
      {children}
    </div>
  );
};

export { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter };

